import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

const QUOTE_STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted" },
  { value: "open", label: "Open" },
];

const DEFAULT_SORT_BY = "rfqDate";
const DEFAULT_SORT_ORDER = "desc";

const SORT_COLUMN_MAP = {
  rfqNumber: "rfqNumber",
  rfqDate: "rfqDate",
  rfqDueDate: "rfqDueDate",
  rfqStatus: "rfqStatus",
  quoteStatus: "quoteStatus",
  quoteSubmittedAt: "quoteSubmittedAt",
};

const SupplierRfqStatCardSkeleton = () => (
  <div className="procurement-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const SUPPLIER_RFQ_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total RFQ",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterValue: null,
  },
  {
    key: "openCount",
    label: "Open",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "open",
  },
  {
    key: "submittedCount",
    label: "Submitted",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterValue: "submitted",
  },
];

const SupplierRfqStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="procurement-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>
        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractSupplierRfqsPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { rfqs: [], pagination: {} };
  }

  if (Array.isArray(dataBlock)) {
    return {
      rfqs: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
    };
  }

  let list = dataBlock.rfqs || root.rfqs || dataBlock.items;

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.count === "number"
            ? dataBlock.count
            : undefined;

  return {
    rfqs: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
  };
}

function formatRfqDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const dateTimeMatch = str.match(/^(\d{2}-\d{2}-\d{4})[ T](\d{2}:\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]} ${dateTimeMatch[2]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const str = String(value).trim();
  const dateTimeMatch = str.match(/^(\d{2}-\d{2}-\d{4})[ T](\d{2}:\d{2})/);
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]} ${dateTimeMatch[2]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
}

function getRfqId(rfq) {
  return rfq?.rfqId || rfq?._id || rfq?.id || "";
}

function rfqNumber(rfq) {
  return rfq?.rfqNumber || rfq?.rfqNo || rfq?.rfqId || "-";
}

function rfqSummaryValue(rfq) {
  const summary = String(rfq?.rfqSummary || rfq?.summary || "").trim();
  return summary || "-";
}

function renderPrNumbers(rfq) {
  const list = Array.isArray(rfq?.prNumbers) ? rfq.prNumbers : [];
  const numbers = [
    ...new Set(
      list.map((item) => String(item || "").trim()).filter(Boolean),
    ),
  ];
  if (numbers.length === 0) return "-";
  return (
    <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {numbers.join(", ")}
    </span>
  );
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value).trim();
  if (!s) return "-";
  return s
    .split(/[\s_-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function renderQuoteStatus(rfq) {
  const status = String(rfq?.quoteStatus || "").trim();
  if (!status) {
    return rfq?.quoteSubmitted ? "Submitted" : "open";
  }
  const normalized = status.toLowerCase();
  let badge = "badge-secondary";
  if (normalized === "submitted") badge = "badge-success";
  if (normalized === "pending") badge = "badge-warning";
  return <span className={`badge ${badge}`}>{capitalizeStatus(status)}</span>;
}

function renderRfqStatus(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  const normalized = value.toLowerCase();
  let badge = "badge-info";
  if (normalized.includes("quotes received")) badge = "badge-primary";
  if (normalized.includes("closed")) badge = "badge-secondary";
  if (normalized.includes("open")) badge = "badge-success";
  if (normalized.includes("po created")) badge = "badge-warning";
  return <span className={`badge ${badge}`}>{value}</span>;
}

const RfqSupplierList = () => {
  const savedFilters = JSON.parse(
    sessionStorage.getItem("rfqSupplierListFilters") || "{}",
  );

  const [rfqs, setRfqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRfqCountLoading, setIsRfqCountLoading] = useState(true);
  const [rfqCount, setRfqCount] = useState({
    totalCount: 0,
    openCount: 0,
    submittedCount: 0,
  });
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [quoteStatus, setQuoteStatus] = useState(
    savedFilters.quoteStatus || "",
  );
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(
    savedFilters.sortOrder || DEFAULT_SORT_ORDER,
  );
  const filterPanelRef = useRef(null);
  const loadRequestIdRef = useRef(0);

  const anyFilterActive = Boolean(quoteStatus);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      quoteStatus,
      sortBy,
      sortOrder,
    };
    sessionStorage.setItem("rfqSupplierListFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    quoteStatus,
    sortBy,
    sortOrder,
  ]);

  const fetchRfqCount = useCallback(
    async (requestId) => {
      setIsRfqCountLoading(true);
      try {
        const currentUserId = localStorage.getItem("id") || undefined;
        const payload = {
          action: "supplier-rfq-count",
          currentUserId,
        };

        const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
        if (trimmedSearch) {
          payload.search = trimmedSearch;
        }

        const response = await axios.post(API_URL, payload);
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );

        if (isApiSuccess(responseData)) {
          const data =
            responseData.data && typeof responseData.data === "object"
              ? responseData.data
              : {};
          setRfqCount({
            totalCount: Number(data.totalCount) || 0,
            openCount: Number(data.openCount) || 0,
            submittedCount: Number(data.submittedCount) || 0,
          });
        }
      } catch (error) {
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }
        console.error("Supplier RFQ Stats Error:", error);
      } finally {
        if (
          typeof requestId !== "number" ||
          requestId === loadRequestIdRef.current
        ) {
          setIsRfqCountLoading(false);
        }
      }
    },
    [activeSearchQuery],
  );

  const fetchRfqs = useCallback(
    async (requestId) => {
      setIsLoading(true);
      try {
        const currentUserId = localStorage.getItem("id") || undefined;
        const payload = {
          action: "get-supplier-rfqs",
          currentUserId,
          page: currentPage,
          limit: itemsPerPage,
        };

        const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
        if (trimmedSearch) {
          payload.search = trimmedSearch;
        }
        if (quoteStatus) {
          payload.quoteStatus = quoteStatus;
        }
        if (sortBy) {
          payload.sortBy = sortBy;
          payload.sortOrder = sortOrder;
        }

        const response = await axios.post(API_URL, payload);
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }

        const responseData = normalizeApiPayload(
          parseApiResponse(response.data),
        );
        const { rfqs: list, pagination } =
          extractSupplierRfqsPayload(responseData);

        const ok =
          isApiSuccess(responseData) ||
          (Array.isArray(list) && list.length > 0);

        if (ok) {
          setRfqs(list);
          const total =
            typeof pagination.total === "number"
              ? pagination.total
              : typeof pagination.totalCount === "number"
                ? pagination.totalCount
                : list.length;
          setTotalCount(total);
        } else {
          toast.error(
            responseData?.message || "Failed to fetch supplier RFQs",
            { autoClose: 2000 },
          );
          setRfqs([]);
          setTotalCount(0);
        }
      } catch (error) {
        if (
          typeof requestId === "number" &&
          requestId !== loadRequestIdRef.current
        ) {
          return;
        }
        console.error("Fetch supplier RFQs error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(
          errData?.message || "An error occurred while fetching supplier RFQs",
          { autoClose: 2000 },
        );
        setRfqs([]);
        setTotalCount(0);
      } finally {
        if (
          typeof requestId !== "number" ||
          requestId === loadRequestIdRef.current
        ) {
          setIsLoading(false);
        }
      }
    },
    [
      currentPage,
      itemsPerPage,
      activeSearchQuery,
      quoteStatus,
      sortBy,
      sortOrder,
    ],
  );

  // Load order: 1) supplier-rfq-count → 2) get-supplier-rfqs
  useEffect(() => {
    const requestId = ++loadRequestIdRef.current;
    let cancelled = false;

    const loadInOrder = async () => {
      await fetchRfqCount(requestId);
      if (cancelled || requestId !== loadRequestIdRef.current) return;
      await fetchRfqs(requestId);
    };

    loadInOrder();

    return () => {
      cancelled = true;
    };
  }, [fetchRfqs, fetchRfqCount]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = rfqs.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem =
    rfqs.length > 0 ? Math.min(currentPage * itemsPerPage, totalCount) : 0;
  const hasActiveFilters = Boolean(activeSearchQuery || anyFilterActive);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleQuoteStatusChange = (value) => {
    setQuoteStatus(value);
    setCurrentPage(1);
  };

  const isRfqStatCardActive = (card) => {
    if (!card.filterValue) {
      return !quoteStatus;
    }
    return quoteStatus === card.filterValue;
  };

  const handleRfqStatCardClick = (card) => {
    if (!card.filterValue) {
      setQuoteStatus("");
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }
    setQuoteStatus(card.filterValue);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setQuoteStatus("");
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearFilter();
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const handleSort = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = SORT_COLUMN_MAP[column] || column;
    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1 text-muted"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className={`fa-solid ${
          sortOrder === "asc" ? "fa-sort-up" : "fa-sort-down"
        } ml-1`}
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  const renderSortableHeader = (column, label, extraStyle = {}) => (
    <th
      onClick={() => handleSort(column)}
      style={{
        cursor: "pointer",
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">RFQ</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row rfq-stat-card-row">
                {isRfqCountLoading
                  ? SUPPLIER_RFQ_STAT_CARDS.map((card) => (
                      <SupplierRfqStatCardSkeleton key={card.key} />
                    ))
                  : SUPPLIER_RFQ_STAT_CARDS.map((card) => (
                      <SupplierRfqStatCard
                        key={card.key}
                        label={card.label}
                        value={rfqCount[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isRfqStatCardActive(card)}
                        onClick={() => handleRfqStatCardClick(card)}
                      />
                    ))}
              </div>
              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4 flex-xl-nowrap">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search RFQs..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label
                          className="mb-1"
                          style={{
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Filter
                        </label>
                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {anyFilterActive ? "Filters" : "Select Filters"}
                          </span>
                          <i
                            className={`fa-solid fa-chevron-${
                              isFilterPanelOpen ? "up" : "down"
                            }`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            className="rfq-filter-dropdown"
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="filterCheckQuoteStatus"
                                checked={Boolean(quoteStatus)}
                                onChange={(e) => {
                                  if (!e.target.checked) {
                                    handleQuoteStatusChange("");
                                  } else if (!quoteStatus) {
                                    handleQuoteStatusChange("submitted");
                                  }
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="filterCheckQuoteStatus"
                              >
                                Quote Status
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {Boolean(quoteStatus) && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{
                              fontSize: "13px",
                              fontWeight: "500",
                            }}
                          >
                            Quote Status
                          </label>
                          <select
                            className="form-control"
                            value={quoteStatus}
                            onChange={(e) =>
                              handleQuoteStatusChange(e.target.value)
                            }
                          >
                            <option value="">Select Quote Status</option>
                            {QUOTE_STATUS_OPTIONS.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {anyFilterActive && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearFilter}
                          style={{
                            minWidth: "100px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear Filter
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading && rfqs.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : rfqs.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no RFQs yet.
                </div>
              ) : rfqs.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No RFQs found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}
                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="supplierRfqs"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              whiteSpace: "nowrap",
                              width: "40px",
                              minWidth: "40px",
                            }}
                          >
                            S.No
                          </th>
                          {renderSortableHeader("rfqNumber", "RFQ No", {
                            minWidth: "120px",
                          })}
                          {renderSortableHeader("rfqDate", "RFQ Date", {
                            minWidth: "100px",
                          })}
                          {renderSortableHeader("rfqDueDate", "RFQ Due Date", {
                            minWidth: "130px",
                          })}
                          <th style={{ minWidth: "100px" }}>PR Number</th>
                          <th style={{ minWidth: "180px" }}>Summary</th>
                          {renderSortableHeader("rfqStatus", "RFQ Status", {
                            minWidth: "120px",
                          })}
                          {renderSortableHeader("quoteStatus", "Quote Status", {
                            minWidth: "110px",
                          })}
                          {renderSortableHeader(
                            "quoteSubmittedAt",
                            "Submitted At",
                            { minWidth: "130px" },
                          )}
                          <th style={{ width: "80px", minWidth: "80px" }}>
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rfqs.map((rfq, index) => {
                          const rid = getRfqId(rfq);
                          return (
                            <tr key={rid || index}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {rfqNumber(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatRfqDate(rfq?.rfqDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatRfqDate(rfq?.rfqDueDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "140px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {renderPrNumbers(rfq)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {rfqSummaryValue(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderRfqStatus(rfq?.rfqStatus)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {renderQuoteStatus(rfq)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatDateTime(rfq?.quoteSubmittedAt)}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  <Link
                                    to={`/rfqSupplierView/${rid}`}
                                    className="crsr_pntr"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {rfqs.length > 0 && (
                    <div
                      className="supply-pagination-container"
                      style={{
                        opacity: isLoading ? 0.65 : 1,
                        pointerEvents: isLoading ? "none" : "auto",
                      }}
                    >
                      <div className="pagination-wrapper">
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>
                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>
                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left" />
                              </button>
                            </li>
                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right" />
                              </button>
                            </li>
                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RfqSupplierList;
